// src/components/Footer/index.js
import React from "react";
import { FaGithub, FaLinkedin, FaEnvelope, FaInstagram } from "react-icons/fa";
import { MapPin, Mail, Mountain, PawPrint } from "lucide-react";
import logo from "../../assets/images/logo-hero.webp";

const Footer = () => {
  return (
    <footer className="relative bg-black text-white py-16">
      {/* Texture Overlay using CSS */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            radial-gradient(#ffffff 0.5px, transparent 0.5px), 
            radial-gradient(#ffffff 0.5px, transparent 0.5px)
          `,
          backgroundSize: "20px 20px",
          backgroundPosition: "0 0, 10px 10px",
        }}
      />

      {/* Alternative pattern - uncomment to use */}
      {/* <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `
            linear-gradient(45deg, #ffffff 25%, transparent 25%),
            linear-gradient(-45deg, #ffffff 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #ffffff 75%),
            linear-gradient(-45deg, transparent 75%, #ffffff 75%)
          `,
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
        }}
      /> */}
      {/* Content Container */}
      <div className="relative z-10 max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="space-y-4">
            <a href="#home" aria-label="Webtrova home">
              <img src={logo} alt="Webtrova logo" width={144} height={144} className="w-20 h-20 md:w-36 md:h-36" />
            </a>
          </div>

          {/* Logo & Description */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-[#b2f141] font-handwriting">
              Webtrova
            </h3>
            <p className="text-gray-400">
              Web oriented programming with functionality in mind
            </p>
            {/* Social Icons */}
            <div className="flex space-x-4 pt-4">
              <a
                href="https://github.com/webtrova"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Webtrova on GitHub"
                className="text-gray-400 hover:text-white transition-all hover:-translate-y-0.5"
              >
                <FaGithub size={24} />
              </a>
              <a
                href="https://www.linkedin.com/in/martinez-pedro-webtrova/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Webtrova on LinkedIn"
                className="text-gray-400 hover:text-white transition-all hover:-translate-y-0.5"
              >
                <FaLinkedin size={24} />
              </a>
              <a
                href="https://www.instagram.com/webtrova/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Webtrova on Instagram"
                className="text-gray-400 hover:text-white transition-all hover:-translate-y-0.5"
              >
                <FaInstagram size={24} />
              </a>
              <a
                href="mailto:pmartinez@webtrova.com"
                aria-label="Email Webtrova"
                className="text-gray-400 hover:text-white transition-all hover:-translate-y-0.5"
              >
                <FaEnvelope size={24} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xl font-semibold mb-4 relative inline-block text-[#b2f141] font-handwriting">
              Quick Links
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#b2f141] to-transparent"></span>
            </h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="/"
                  className="inline-block text-gray-400 hover:text-white transition-all hover:translate-x-1"
                >
                  Home
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  className="inline-block text-gray-400 hover:text-white transition-all hover:translate-x-1"
                >
                  About
                </a>
              </li>
              <li>
                <a
                  href="/projects"
                  className="inline-block text-gray-400 hover:text-white transition-all hover:translate-x-1"
                >
                  Work
                </a>
              </li>
              <li>
                <a
                  href="/contact"
                  className="inline-block text-gray-400 hover:text-lime-400 transition-all hover:translate-x-1"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xl font-semibold mb-4 text-[#b2f141] relative inline-block font-handwriting">
              Contact
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#b2f141] to-transparent"></span>
            </h4>
            <ul className="space-y-2">
              <li className="flex items-center text-gray-400">
                <MapPin className="w-4 h-4 mr-2 text-[#b2f141]" /> New Jersey
              </li>
              <li className="flex items-center text-gray-400">
                <Mail className="w-4 h-4 mr-2 text-[#b2f141]" /> info@webtrova.com
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Webtrova. All rights reserved.
            </p>
            <div className="flex space-x-4 mt-4 md:mt-0 items-center">
              <a href="/privacy" className="text-gray-400 hover:text-white text-sm">
                Privacy Policy
              </a>
              <a href="/terms" className="text-gray-400 hover:text-white text-sm">
                Terms of Service
              </a>
              {/* Creative Cave Link - Wolf Easter Egg */}
              <a
                href="/admin/login"
                className="group relative text-gray-600 hover:text-[#b2f142] hover:scale-110 text-sm transition-all flex items-center gap-1"
                title="Enter the Creation Cave"
                aria-label="Enter the Creation Cave"
              >
                <PawPrint className="w-5 h-5" />
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#262626] text-[#b2f142] px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none flex items-center gap-1">
                  <Mountain className="w-3 h-3" />
                  Enter the Cave
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
